import { useEffect, useState } from "react";
import { Trash2, X, Save, Pause, Play } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, CATEGORIES } from "@/lib/api";
import { toast } from "sonner";

/**
 * Generic edit-drawer for PRO items.
 * `type` picks the field schema and API endpoints:
 *   - "income" / "expense" / "goal" / "recurring" / "subscription" / "template"
 *
 * Props:
 * - open, onOpenChange
 * - item — the full item object being edited (must include `.id`)
 * - type — one of the strings above
 * - onSaved(updated) — called after successful PATCH
 * - onDeleted(id) — called after successful DELETE
 */
const FREQS = ["weekly", "biweekly", "monthly", "quarterly", "yearly"];
const REC_FREQS = ["daily", "weekly", "monthly", "yearly"];
const SPLIT_TYPES = ["equal", "exact", "percent", "shares"];

const CONFIG = {
  income:   { title: "Edit income",       endpoint: "/incomes",         fields: ["source", "amount", "frequency", "expected_day_of_month", "is_primary", "notes"] },
  expense:  { title: "Edit expense",      endpoint: "/expenses",        fields: ["title", "amount", "category", "date", "notes"] },
  goal:     { title: "Edit savings goal", endpoint: "/savings-goals",   fields: ["name", "emoji", "target_amount", "current_amount", "target_date", "notes"] },
  recurring:{ title: "Edit recurring",    endpoint: "/recurring-bills", fields: ["title", "amount", "frequency", "next_due", "category", "paused", "notes"] },
  subscription: { title: "Edit subscription", endpoint: "/recurring-bills", fields: ["title", "amount", "frequency", "next_due", "paused", "notes"] },
  template: { title: "Edit template",     endpoint: "/split-templates", fields: ["name", "split_type", "default_amount", "schedule", "notes"] },
};

const F = ({ children }) => <div className="mb-3">{children}</div>;

export const PROItemEditDrawer = ({ open, onOpenChange, item, type, onSaved, onDeleted, currency = "USD" }) => {
  const cfg = CONFIG[type];
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({ ...item });
      setConfirmDel(false);
    }
  }, [item]);

  if (!cfg || !item) return null;
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const f of cfg.fields) {
        if (form[f] !== undefined && form[f] !== item[f]) payload[f] = form[f];
      }
      if (Object.keys(payload).length === 0) { onOpenChange(false); return; }
      const { data } = await api.patch(`${cfg.endpoint}/${item.id}`, payload);
      toast.success("Saved");
      onSaved?.(data || { ...item, ...payload });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async () => {
    setSaving(true);
    try {
      await api.delete(`${cfg.endpoint}/${item.id}`);
      toast.success("Removed");
      onDeleted?.(item.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setSaving(false); }
  };

  const togglePaused = async () => {
    const paused = !form.paused;
    try {
      await api.patch(`${cfg.endpoint}/${item.id}`, { paused });
      setField("paused", paused);
      toast.success(paused ? "Paused" : "Resumed");
      onSaved?.({ ...item, ...form, paused });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const dateInputVal = (v) => {
    if (!v) return "";
    try { return String(v).slice(0, 10); } catch { return ""; }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-card border-l border-border w-full sm:max-w-md overflow-y-auto" data-testid={`pro-edit-${type}`}>
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl">{cfg.title}</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {cfg.fields.includes("source") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Source</Label>
              <Input value={form.source ?? ""} onChange={(e) => setField("source", e.target.value)} className="mt-1.5 bg-muted border-border h-11" data-testid="pe-source" /></F>
          )}
          {cfg.fields.includes("title") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Title</Label>
              <Input value={form.title ?? ""} onChange={(e) => setField("title", e.target.value)} className="mt-1.5 bg-muted border-border h-11" data-testid="pe-title" /></F>
          )}
          {cfg.fields.includes("name") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Name</Label>
              <Input value={form.name ?? ""} onChange={(e) => setField("name", e.target.value)} className="mt-1.5 bg-muted border-border h-11" data-testid="pe-name" /></F>
          )}
          {cfg.fields.includes("amount") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Amount ({currency})</Label>
              <Input type="number" step="0.01" value={form.amount ?? ""} onChange={(e) => setField("amount", Number(e.target.value))} className="mt-1.5 bg-muted border-border h-11 font-mono" data-testid="pe-amount" /></F>
          )}
          {cfg.fields.includes("target") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Target</Label>
              <Input type="number" step="0.01" value={form.target ?? ""} onChange={(e) => setField("target", Number(e.target.value))} className="mt-1.5 bg-muted border-border h-11 font-mono" data-testid="pe-target" /></F>
          )}
          {cfg.fields.includes("target_amount") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Target amount</Label>
              <Input type="number" step="0.01" value={form.target_amount ?? ""} onChange={(e) => setField("target_amount", Number(e.target.value))} className="mt-1.5 bg-muted border-border h-11 font-mono" data-testid="pe-target-amount" /></F>
          )}
          {cfg.fields.includes("current") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Current amount</Label>
              <Input type="number" step="0.01" value={form.current ?? 0} onChange={(e) => setField("current", Number(e.target.value))} className="mt-1.5 bg-muted border-border h-11 font-mono" data-testid="pe-current" /></F>
          )}
          {cfg.fields.includes("current_amount") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Current amount</Label>
              <Input type="number" step="0.01" value={form.current_amount ?? 0} onChange={(e) => setField("current_amount", Number(e.target.value))} className="mt-1.5 bg-muted border-border h-11 font-mono" data-testid="pe-current-amount" /></F>
          )}
          {cfg.fields.includes("emoji") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Emoji</Label>
              <Input value={form.emoji ?? ""} onChange={(e) => setField("emoji", e.target.value)} maxLength={2} className="mt-1.5 bg-muted border-border h-11 text-xl w-20" data-testid="pe-emoji" /></F>
          )}
          {cfg.fields.includes("target_date") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Target date</Label>
              <Input type="date" value={dateInputVal(form.target_date)} onChange={(e) => setField("target_date", new Date(e.target.value).toISOString())} className="mt-1.5 bg-muted border-border h-11" data-testid="pe-target-date" /></F>
          )}
          {cfg.fields.includes("default_amount") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Default amount</Label>
              <Input type="number" step="0.01" value={form.default_amount ?? ""} onChange={(e) => setField("default_amount", e.target.value ? Number(e.target.value) : null)} className="mt-1.5 bg-muted border-border h-11 font-mono" data-testid="pe-default-amount" /></F>
          )}
          {cfg.fields.includes("frequency") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Frequency</Label>
              <Select value={form.frequency ?? ""} onValueChange={(v) => setField("frequency", v)}>
                <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="pe-frequency"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {(type === "income" ? FREQS : REC_FREQS).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select></F>
          )}
          {cfg.fields.includes("split_type") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Split type</Label>
              <Select value={form.split_type ?? "equal"} onValueChange={(v) => setField("split_type", v)}>
                <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="pe-split-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {SPLIT_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select></F>
          )}
          {cfg.fields.includes("schedule") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Schedule</Label>
              <Select value={form.schedule ?? "once"} onValueChange={(v) => setField("schedule", v)}>
                <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="pe-schedule"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="once">One-time</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select></F>
          )}
          {cfg.fields.includes("expected_day_of_month") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Day of month</Label>
              <Input type="number" min="1" max="31" value={form.expected_day_of_month ?? ""} onChange={(e) => setField("expected_day_of_month", e.target.value ? Number(e.target.value) : null)} className="mt-1.5 bg-muted border-border h-11 font-mono" data-testid="pe-dom" /></F>
          )}
          {cfg.fields.includes("category") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Category</Label>
              <Select value={form.category ?? "general"} onValueChange={(v) => setField("category", v)}>
                <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="pe-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {(CATEGORIES || []).map((c) => <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>)}
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                </SelectContent>
              </Select></F>
          )}
          {cfg.fields.includes("date") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Date</Label>
              <Input type="date" value={dateInputVal(form.date)} onChange={(e) => setField("date", new Date(e.target.value).toISOString())} className="mt-1.5 bg-muted border-border h-11" data-testid="pe-date" /></F>
          )}
          {cfg.fields.includes("next_due") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Next due</Label>
              <Input type="date" value={dateInputVal(form.next_due)} onChange={(e) => setField("next_due", new Date(e.target.value).toISOString())} className="mt-1.5 bg-muted border-border h-11" data-testid="pe-next-due" /></F>
          )}
          {cfg.fields.includes("deadline") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Deadline</Label>
              <Input type="date" value={dateInputVal(form.deadline)} onChange={(e) => setField("deadline", new Date(e.target.value).toISOString())} className="mt-1.5 bg-muted border-border h-11" data-testid="pe-deadline" /></F>
          )}
          {cfg.fields.includes("is_primary") && (
            <div className="flex items-center justify-between rounded-lg border border-border bg-foreground/[0.02] p-3">
              <div><div className="text-sm">Primary source</div><div className="text-[11px] text-muted-foreground">Mark as your main income</div></div>
              <Switch checked={!!form.is_primary} onCheckedChange={(v) => setField("is_primary", v)} data-testid="pe-primary" />
            </div>
          )}
          {cfg.fields.includes("paused") && (
            <div className="flex items-center justify-between rounded-lg border border-border bg-foreground/[0.02] p-3">
              <div className="flex items-center gap-2">
                {form.paused ? <Pause className="h-4 w-4 text-amber-400" strokeWidth={1.5} /> : <Play className="h-4 w-4 brand-text" strokeWidth={1.5} />}
                <div><div className="text-sm">{form.paused ? "Paused" : "Active"}</div><div className="text-[11px] text-muted-foreground">Skip auto-billing until resumed</div></div>
              </div>
              <Switch checked={!!form.paused} onCheckedChange={togglePaused} data-testid="pe-paused" />
            </div>
          )}
          {cfg.fields.includes("notes") && (
            <F><Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Notes</Label>
              <Textarea rows={2} value={form.notes ?? ""} onChange={(e) => setField("notes", e.target.value)} className="mt-1.5 bg-muted border-border" data-testid="pe-notes" /></F>
          )}
        </div>

        <div className="mt-8 flex flex-col gap-2 pb-4">
          <Button onClick={save} disabled={saving} className="brand-bg text-black hover:opacity-90 h-12 w-full" data-testid="pe-save">
            <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> {saving ? "Saving…" : "Save changes"}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border h-11 w-full" data-testid="pe-cancel">
            <X className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Cancel
          </Button>
          {!confirmDel ? (
            <Button variant="outline" onClick={() => setConfirmDel(true)} className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-11 w-full mt-3" data-testid="pe-delete-init">
              <Trash2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Delete entry
            </Button>
          ) : (
            <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 mt-3 space-y-2" data-testid="pe-delete-confirm">
              <div className="text-xs text-muted-foreground">This cannot be undone.</div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setConfirmDel(false)} className="border-border h-10 flex-1">Cancel</Button>
                <Button onClick={del} disabled={saving} className="bg-red-500 text-white hover:bg-red-600 h-10 flex-1" data-testid="pe-delete-confirm-btn">Delete forever</Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
